import { 
  createBrowserRouter, 
  RouterProvider, 
  Navigate,
  Outlet,
  useRouteError,
  isRouteErrorResponse
} from 'react-router-dom';
import { lazy, Suspense } from 'react';
import AppShell from '@/components/layout/AppShell';
import RoutePreloader from '@/components/common/RoutePreloader';
import DebugPage from '@/pages/DebugPage';

// 懒加载页面组件
const HomePage = lazy(() => import('@/pages/HomePage'));
const DiscoverPage = lazy(() => import('@/pages/DiscoverPage'));
const FavoritesPage = lazy(() => import('@/pages/FavoritesPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const CorsTestPage = lazy(() => import('@/pages/CorsTestPage'));

// 阅读相关页面组件
const BookLibraryPage = lazy(() => import('@/pages/reading/BookLibraryPage'));
const BookSourcesPage = lazy(() => import('@/pages/reading/BookSourcesPage'));
const BookReaderPage = lazy(() => import('@/pages/reading/BookReaderPage'));
const CacheSettingsPage = lazy(() => import('@/pages/reading/CacheSettings'));
const ReadingWorkflowDemoPage = lazy(() => import('@/pages/reading/ReadingWorkflowDemo'));
const SystemStatusPage = lazy(() => import('@/pages/reading/SystemStatus'));

// 页面加载组件
const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    padding: '2rem',
    fontSize: '16px',
    color: '#666'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    }}>
      <div style={{
        width: '20px',
        height: '20px',
        border: '2px solid #e0e0e0',
        borderTop: '2px solid #007AFF',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <span>页面加载中...</span>
    </div>
  </div>
);

// 错误边界组件
const ErrorBoundary = () => {
  const error = useRouteError();
  
  let errorMessage: string;
  let errorStatus: number | undefined;

  if (isRouteErrorResponse(error)) {
    errorMessage = error.data?.message || error.statusText || '页面出现错误';
    errorStatus = error.status;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else {
    errorMessage = '未知错误';
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '2rem',
      textAlign: 'center',
      background: 'var(--color-background-primary, #f8f9fa)'
    }}>
      <div style={{
        maxWidth: '500px',
        padding: '2rem',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{
          fontSize: '2rem',
          color: '#FF3B30',
          marginBottom: '1rem'
        }}>
          {errorStatus ? `错误 ${errorStatus}` : '页面错误'}
        </h1>
        <p style={{
          fontSize: '1.1rem',
          color: '#666',
          marginBottom: '2rem',
          lineHeight: '1.5'
        }}>
          {errorMessage}
        </p>
        <button
          onClick={() => window.location.href = '/'}
          style={{
            padding: '12px 24px',
            background: '#007AFF',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0056CC'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#007AFF'}
        >
          返回首页
        </button>
      </div>
    </div>
  );
};

// 根布局组件 - 包含AppShell和RoutePreloader，确保路由hooks可以正常工作
const RootLayout = () => {
  return (
    <AppShell>
      {/* 路由预加载器 - 现在在Router上下文内 */}
      <RoutePreloader preloadOnHover={true} />
      {/* <PageTransition> */}
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      {/* </PageTransition> */}
    </AppShell>
  );
};

// 路由配置
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <HomePage />,
        handle: {
          title: '首页',
          description: 'Dopamine Web App 主页'
        }
      },
      {
        path: 'discover',
        element: <DiscoverPage />,
        handle: {
          title: '发现',
          description: '发现新内容和功能'
        }
      },
      {
        path: 'favorites',
        element: <FavoritesPage />,
        handle: {
          title: '收藏',
          description: '我的收藏内容'
        }
      },
      {
        path: 'about',
        element: <AboutPage />,
        handle: {
          title: '关于',
          description: '关于 Dopamine Web App'
        }
      },
      {
        path: 'debug',
        element: <DebugPage />,
        handle: {
          title: '调试',
          description: '调试页面'
        }
      },
      {
        path: 'cors-test',
        element: <CorsTestPage />,
        handle: {
          title: 'CORS 测试',
          description: '跨域请求功能测试'
        }
      },
      // 阅读相关路由
      {
        path: 'library',
        element: <BookLibraryPage />,
        handle: {
          title: '书库',
          description: '我的图书库 - 浏览和管理图书'
        }
      },
      {
        path: 'sources',
        element: <BookSourcesPage />,
        handle: {
          title: '书源管理',
          description: '管理图书源 - 添加和配置书源'
        }
      },
      {
        path: 'reader/:bookId',
        element: <BookReaderPage />,
        handle: {
          title: '阅读器',
          description: '图书阅读界面'
        }
      },
      {
        path: 'reader/:bookId/:chapterId',
        element: <BookReaderPage />,
        handle: {
          title: '阅读器',
          description: '图书阅读界面'
        }
      },
      {
        path: 'cache-settings',
        element: <CacheSettingsPage />,
        handle: {
          title: '缓存设置',
          description: '缓存管理和配置'
        }
      },
      {
        path: 'workflow-demo',
        element: <ReadingWorkflowDemoPage />,
        handle: {
          title: '系统集成演示',
          description: '完整用户工作流演示'
        }
      },
      {
        path: 'system-status',
        element: <SystemStatusPage />,
        handle: {
          title: '系统状态',
          description: '系统健康状态监控'
        }
      },
      // 404 处理
      {
        path: '*',
        element: <Navigate to="/" replace />
      }
    ]
  }
], {
  future: {
    // 启用 React Router v7 的未来特性
    v7_startTransition: true,
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true
  }
});

// 导出路由器
const AppRoutes = () => {
  return <RouterProvider router={router} />;
};

export default AppRoutes;